import { createHttpService } from 'src/apis/ApiService';
import { TPaginationParams } from 'src/apis/__shared/shared.interfaces';
import {
  mockGuardianAssessments,
  mockGuardianHomework,
  mockGuardianAttendance,
  mockGuardianLibraryBooks,
  mockGuardianSchedule,
  mockGuardianClass,
  mockGuardianCourses,
  mockGuardianLessonPlans,
} from 'src/__mocks__/guardian/guardianAcademic.mock';
import { IScheduleListResponse, IClassListResponse, ICourseListResponse, ILessonPlanListResponse } from 'src/apis/academics/academics.interfaces';
import isAppWithMock from 'src/utils/isAppWithMock';

export type GuardianAssessment = {
  id: string;
  subject: string;
  title: string;
  score: number;
  maxScore: number;
  date: string;
  grade: string;
};

export type GuardianHomework = {
  id: string;
  subject: string;
  title: string;
  dueDate: string;
  status: 'completed' | 'pending' | 'overdue';
  description: string;
};

export type GuardianAttendance = {
  date: string;
  status: 'present' | 'absent' | 'late';
  period: string;
};

export type GuardianLibraryBook = {
  id: string;
  title: string;
  author: string;
  isbn: string;
  available: boolean;
  dueDate?: string;
};

const useGuardianAcademicService = () => {
  const httpAPI = createHttpService();
  const baseGuardianPath = '/guardian/academic';
  const withMockData = isAppWithMock();

  const getAssessments = (
    studentId: string,
    params?: TPaginationParams & { subject?: string; fromDate?: string; toDate?: string }
  ): Promise<{ data: GuardianAssessment[]; count: number }> => {
    if (withMockData) {
      let filteredData = [...mockGuardianAssessments];
      
      if (params?.subject) {
        filteredData = filteredData.filter((a) => a.subject === params.subject);
      }
      if (params?.fromDate) {
        filteredData = filteredData.filter((a) => a.date >= params.fromDate!);
      }
      if (params?.toDate) {
        filteredData = filteredData.filter((a) => a.date <= params.toDate!);
      }
      
      const start = params?.page && params?.limit ? (params.page - 1) * params.limit : 0;
      const end = params?.limit ? start + params.limit : filteredData.length;
      const paginatedData = filteredData.slice(start, end);
      
      return Promise.resolve({
        data: paginatedData,
        count: filteredData.length,
      });
    }

    const requestObj = {
      params: {
        studentId,
        ...(params?.page && { page: Number(params.page) }),
        ...(params?.limit && { limit: Number(params.limit) }),
      },
    };

    return httpAPI
      .get<{ data: GuardianAssessment[]; count: number }>(
        `${baseGuardianPath}/assessments`,
        requestObj
      )
      .then((response) => {
        const result = response;
        if (Array.isArray(result)) {
          return { data: result, count: result.length };
        }
        return result as { data: GuardianAssessment[]; count: number };
      })
      .catch((error) => {
        console.error('Error fetching guardian assessments:', error);
        return { data: [], count: 0 };
      });
  };

  const getHomework = (
    studentId: string,
    params?: TPaginationParams & { subject?: string; status?: string }
  ): Promise<{ data: GuardianHomework[]; count: number }> => {
    if (withMockData) {
      let filteredData = [...mockGuardianHomework];
      
      if (params?.subject) {
        filteredData = filteredData.filter((h) => h.subject === params.subject);
      }
      if (params?.status) {
        filteredData = filteredData.filter((h) => h.status === params.status);
      }
      
      const start = params?.page && params?.limit ? (params.page - 1) * params.limit : 0;
      const end = params?.limit ? start + params.limit : filteredData.length;
      const paginatedData = filteredData.slice(start, end);
      
      return Promise.resolve({
        data: paginatedData,
        count: filteredData.length,
      });
    }

    const requestObj = {
      params: {
        studentId,
        ...(params?.page && { page: Number(params.page) }),
        ...(params?.limit && { limit: Number(params.limit) }),
        ...(params?.subject && { subject: params.subject }),
        ...(params?.status && { status: params.status }),
      },
    };

    return httpAPI
      .get<{ data: GuardianHomework[]; count: number }>(
        `${baseGuardianPath}/homework`,
        requestObj
      )
      .then((response) => {
        const result = response;
        if (Array.isArray(result)) {
          return { data: result, count: result.length };
        }
        return result as { data: GuardianHomework[]; count: number };
      })
      .catch((error) => {
        console.error('Error fetching guardian homework:', error);
        return { data: [], count: 0 };
      });
  };

  const getAttendance = (
    studentId: string,
    params?: TPaginationParams & { fromDate?: string; toDate?: string }
  ): Promise<{ data: GuardianAttendance[]; count: number }> => {
    if (withMockData) {
      let filteredData = [...mockGuardianAttendance];
      
      if (params?.fromDate) {
        filteredData = filteredData.filter((a) => a.date >= params.fromDate!);
      }
      if (params?.toDate) {
        filteredData = filteredData.filter((a) => a.date <= params.toDate!);
      }
      
      const start = params?.page && params?.limit ? (params.page - 1) * params.limit : 0;
      const end = params?.limit ? start + params.limit : filteredData.length;
      const paginatedData = filteredData.slice(start, end);
      
      return Promise.resolve({
        data: paginatedData,
        count: filteredData.length,
      });
    }

    const requestObj = {
      params: {
        studentId,
        ...(params?.page && { page: Number(params.page) }),
        ...(params?.limit && { limit: Number(params.limit) }),
        ...(params?.fromDate && { fromDate: params.fromDate }),
        ...(params?.toDate && { toDate: params.toDate }),
      },
    };

    return httpAPI
      .get<{ data: GuardianAttendance[]; count: number }>(
        `${baseGuardianPath}/attendance`,
        requestObj
      )
      .then((response) => {
        const result = response;
        if (Array.isArray(result)) {
          return { data: result, count: result.length };
        }
        return result as { data: GuardianAttendance[]; count: number };
      })
      .catch((error) => {
        console.error('Error fetching guardian attendance:', error);
        return { data: [], count: 0 };
      });
  };

  const getLibraryBooks = (
    studentId: string,
    params?: TPaginationParams
  ): Promise<{ data: GuardianLibraryBook[]; count: number }> => {
    if (withMockData) {
      const filteredData = [...mockGuardianLibraryBooks];
      
      const start = params?.page && params?.limit ? (params.page - 1) * params.limit : 0;
      const end = params?.limit ? start + params.limit : filteredData.length;
      const paginatedData = filteredData.slice(start, end);
      
      return Promise.resolve({
        data: paginatedData,
        count: filteredData.length,
      });
    }

    const requestObj = {
      params: {
        studentId,
        ...(params?.page && { page: Number(params.page) }),
        ...(params?.limit && { limit: Number(params.limit) }),
      },
    };

    return httpAPI
      .get<{ data: GuardianLibraryBook[]; count: number }>(
        `${baseGuardianPath}/library`,
        requestObj
      )
      .then((response) => {
        const result = response;
        if (Array.isArray(result)) {
          return { data: result, count: result.length };
        }
        return result as { data: GuardianLibraryBook[]; count: number };
      })
      .catch((error) => {
        console.error('Error fetching guardian library books:', error);
        return { data: [], count: 0 };
      });
  };

  const getSchedule = (
    studentId: string,
    params?: TPaginationParams & { academicYear?: string; term?: string }
  ): Promise<IScheduleListResponse> => {
    if (withMockData) {
      let filteredSchedules = [...mockGuardianSchedule];
      
      // Filter by academic year if provided
      if (params?.academicYear) {
        filteredSchedules = filteredSchedules.filter(
          (s) => s.academicYear === params.academicYear
        );
      }
      
      // Filter by term if provided
      if (params?.term) {
        filteredSchedules = filteredSchedules.filter(
          (s) => s.term === params.term
        );
      }
      
      // Pagination
      const start = params?.page && params?.limit ? (params.page - 1) * params.limit : 0;
      const end = params?.limit ? start + params.limit : filteredSchedules.length;
      const paginatedSchedules = filteredSchedules.slice(start, end);
      
      return Promise.resolve({
        schedules: paginatedSchedules,
        total: filteredSchedules.length,
        pagination: {
          page: params?.page || 1,
          limit: params?.limit || 10,
          total: filteredSchedules.length,
          totalPages: Math.ceil(filteredSchedules.length / (params?.limit || 10)),
        },
      });
    }

    const requestObj = {
      params: {
        studentId,
        ...(params?.page && { page: Number(params.page) }),
        ...(params?.limit && { limit: Number(params.limit) }),
        ...(params?.academicYear && { academicYear: params.academicYear }),
        ...(params?.term && { term: params.term }),
      },
    };

    return httpAPI
      .get<IScheduleListResponse>(`/academics/schedules`, requestObj)
      .then((response) => {
        return response as IScheduleListResponse;
      })
      .catch((error) => {
        console.error('Error fetching guardian schedule:', error);
        return {
          schedules: [],
          total: 0,
          pagination: {
            page: params?.page || 1,
            limit: params?.limit || 10,
            total: 0,
            totalPages: 0,
          },
        };
      });
  };

  const getCoursesAndClasses = (
    studentId: string,
    params?: TPaginationParams
  ): Promise<{ classes: IClassListResponse; courses: ICourseListResponse }> => {
    if (withMockData) {
      // Pagination for courses
      const start = params?.page && params?.limit ? (params.page - 1) * params.limit : 0;
      const end = params?.limit ? start + params.limit : mockGuardianCourses.length;
      const paginatedCourses = mockGuardianCourses.slice(start, end);
      
      return Promise.resolve({
        classes: {
          classes: [mockGuardianClass],
          total: 1,
          pagination: {
            page: 1,
            limit: 10,
            total: 1,
            totalPages: 1,
          },
        },
        courses: {
          courses: paginatedCourses,
          total: mockGuardianCourses.length,
          pagination: {
            page: params?.page || 1,
            limit: params?.limit || 10,
            total: mockGuardianCourses.length,
            totalPages: Math.ceil(mockGuardianCourses.length / (params?.limit || 10)),
          },
        },
      });
    }

    const requestObj = {
      params: {
        studentId,
        ...(params?.page && { page: Number(params.page) }),
        ...(params?.limit && { limit: Number(params.limit) }),
      },
    };

    // Fetch both classes and courses in parallel
    return Promise.all([
      httpAPI.get<IClassListResponse>(`/academics/classes`, requestObj),
      httpAPI.get<ICourseListResponse>(`/academics/courses`, requestObj),
    ])
      .then(([classesResponse, coursesResponse]) => {
        return {
          classes: classesResponse as IClassListResponse,
          courses: coursesResponse as ICourseListResponse,
        };
      })
      .catch((error) => {
        console.error('Error fetching guardian courses and classes:', error);
        return {
          classes: {
            classes: [],
            total: 0,
            pagination: {
              page: params?.page || 1,
              limit: params?.limit || 10,
              total: 0,
              totalPages: 0,
            },
          },
          courses: {
            courses: [],
            total: 0,
            pagination: {
              page: params?.page || 1,
              limit: params?.limit || 10,
              total: 0,
              totalPages: 0,
            },
          },
        };
      });
  };

  const getLessonPlans = (
    studentId: string,
    params?: TPaginationParams & { courseId?: string; status?: string }
  ): Promise<ILessonPlanListResponse> => {
    if (withMockData) {
      let filteredPlans = [...mockGuardianLessonPlans];

      // Filter by courseId if provided
      if (params?.courseId) {
        filteredPlans = filteredPlans.filter(
          (lp) => lp.course?.id === params.courseId
        );
      }

      // Filter by status if provided
      if (params?.status) {
        filteredPlans = filteredPlans.filter(
          (lp) => lp.status === params.status
        );
      }

      // Pagination
      const start = params?.page && params?.limit ? (params.page - 1) * params.limit : 0;
      const end = params?.limit ? start + params.limit : filteredPlans.length;
      const paginatedPlans = filteredPlans.slice(start, end);

      return Promise.resolve({
        lessonPlans: paginatedPlans,
        total: filteredPlans.length,
        pagination: {
          page: params?.page || 1,
          limit: params?.limit || 10,
          total: filteredPlans.length,
          totalPages: Math.ceil(filteredPlans.length / (params?.limit || 10)),
        },
      });
    }

    const requestObj = {
      params: {
        studentId,
        ...(params?.page && { page: Number(params.page) }),
        ...(params?.limit && { limit: Number(params.limit) }),
        ...(params?.courseId && { courseId: params.courseId }),
        ...(params?.status && { status: params.status }),
      },
    };

    return httpAPI
      .get<ILessonPlanListResponse>(`/academics/lesson-plans`, requestObj)
      .then((response) => {
        return response as ILessonPlanListResponse;
      })
      .catch((error) => {
        console.error('Error fetching guardian lesson plans:', error);
        return {
          lessonPlans: [],
          total: 0,
          pagination: {
            page: params?.page || 1,
            limit: params?.limit || 10,
            total: 0,
            totalPages: 0,
          },
        };
      });
  };

  return {
    getAssessments,
    getHomework,
    getAttendance,
    getLibraryBooks,
    getSchedule,
    getCoursesAndClasses,
    getLessonPlans,
  };
};

export default useGuardianAcademicService;
